import type { EmailTypeMetadata } from "@/lib/utils/crm/communications-webhook-helpers";

export type CommunicationEmailSource = EmailTypeMetadata["source"];

export interface CommunicationEmailProvenance {
  isEmailSync: boolean;
  source: CommunicationEmailSource | null;
  sourceLabel: string | null;
  tags: string[];
  provider: string | null;
  providerLabel: string | null;
  syncedMailbox: string | null;
  contactSelectorEmail: string | null;
  contactSelectorPhone: string | null;
  emailId: string | null;
  threadId: string | null;
  importedVia: string | null;
  indexed: boolean | null;
}

const EMAIL_SYNC_PROVIDERS = new Set(["gmail", "outlook", "outlook-oauth"]);

const SOURCE_LABELS: Record<CommunicationEmailSource, string> = {
  "schooley-mitchell": "Schooley Mitchell",
  "west-tn-consulting": "West Tennessee Consulting",
};

const PROVIDER_LABELS: Record<string, string> = {
  gmail: "Gmail",
  outlook: "Outlook",
  "outlook-oauth": "Outlook (OAuth)",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Normalize GraphQL `metadata` which may arrive as an object or JSON string. */
export function parseCommunicationMetadata(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return isRecord(raw) ? raw : null;
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readBoolean(record: Record<string, unknown>, key: string): boolean | null {
  const value = record[key];
  return typeof value === "boolean" ? value : null;
}

function parseEmailType(record: Record<string, unknown>): EmailTypeMetadata | null {
  const emailType = record.emailType;
  if (!isRecord(emailType)) return null;

  const source = emailType.source;
  if (source !== "schooley-mitchell" && source !== "west-tn-consulting") {
    return null;
  }

  const tags = Array.isArray(emailType.tags)
    ? emailType.tags.filter((tag): tag is string => typeof tag === "string")
    : [];

  return { source, tags };
}

function parseContactSelector(
  record: Record<string, unknown>
): { contactEmail: string | null; contactPhone: string | null } {
  const selector = record.contact_selector;
  if (!isRecord(selector)) {
    return { contactEmail: null, contactPhone: null };
  }

  return {
    contactEmail: readString(selector, "contact_email"),
    contactPhone: readString(selector, "contact_phone"),
  };
}

export function parseCommunicationEmailProvenance(metadata: unknown): CommunicationEmailProvenance {
  const record = parseCommunicationMetadata(metadata);
  const empty: CommunicationEmailProvenance = {
    isEmailSync: false,
    source: null,
    sourceLabel: null,
    tags: [],
    provider: null,
    providerLabel: null,
    syncedMailbox: null,
    contactSelectorEmail: null,
    contactSelectorPhone: null,
    emailId: null,
    threadId: null,
    importedVia: null,
    indexed: null,
  };

  if (!record) return empty;

  const provider = readString(record, "provider");
  const isEmailSync = provider != null && EMAIL_SYNC_PROVIDERS.has(provider);
  if (!isEmailSync) return empty;

  const emailType = parseEmailType(record);
  const { contactEmail, contactPhone } = parseContactSelector(record);

  return {
    isEmailSync: true,
    source: emailType?.source ?? null,
    sourceLabel: emailType ? SOURCE_LABELS[emailType.source] : null,
    tags: emailType?.tags ?? [],
    provider,
    providerLabel: provider ? (PROVIDER_LABELS[provider] ?? provider) : null,
    syncedMailbox: readString(record, "user_email"),
    contactSelectorEmail: contactEmail,
    contactSelectorPhone: contactPhone,
    emailId: readString(record, "emailId"),
    threadId: readString(record, "threadId"),
    importedVia: readString(record, "imported_via"),
    indexed: readBoolean(record, "indexed"),
  };
}

export function getCommunicationEmailSourceBadgeClass(
  source: CommunicationEmailSource | null
): string {
  switch (source) {
    case "schooley-mitchell":
      return "bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-950/50 dark:text-amber-100 dark:border-amber-800";
    case "west-tn-consulting":
      return "bg-blue-100 text-blue-900 border-blue-200 dark:bg-blue-950/50 dark:text-blue-100 dark:border-blue-800";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700";
  }
}
